import {
  addMarkerSelector,
  areaLevelAtom,
  firstAreaAtom,
  mapRefAtom,
  markersAtom,
  secondAreaAtom,
  selectedTimeAtom,
  setSelectedAreaSelector,
  showSecondGeocoderAtom,
  userLocationMarkerAtom,
  viewStateAtom,
} from "@/api/atoms";
import { lastReported } from "@/config";
import type { MarkerType } from "@/types";
import { getZoom } from "@/utils";
import axios from "axios";

import "mapbox-gl/dist/mapbox-gl.css";

import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";
import ReactMapGL, {
  type MapLayerMouseEvent,
  type MapRef,
  type ViewStateChangeEvent,
} from "react-map-gl";

import MapContent from "./MapContent";

const emptyCollection = {
  type: "FeatureCollection",
  features: [],
};

const _defaultGeojson = {
  country: emptyCollection,
  county: emptyCollection,
  city: emptyCollection,
  borough: emptyCollection,
};

const Map = () => {
  const hoveredStateIdRef = useRef<number | null>(null);

  const selectedTime = useAtomValue(selectedTimeAtom);
  const firstArea = useAtomValue(firstAreaAtom);
  const secondArea = useAtomValue(secondAreaAtom);
  const areaLevel = useAtomValue(areaLevelAtom);
  const [, setShowSecondGeocoder] = useAtom(showSecondGeocoderAtom);

  const [viewState, setViewState] = useAtom(viewStateAtom);

  const setSelectedArea = useSetAtom(setSelectedAreaSelector);
  const addMarker = useSetAtom(addMarkerSelector);
  const [markers, setMarkers] = useAtom(markersAtom);
  const [, setUserMarker] = useAtom(userLocationMarkerAtom);

  const [previousLevel, setPreviousLevel] = useState("country");

  const mapRef = useRef<MapRef | null>(null);

  const [map, setMapRef] = useAtom(mapRefAtom);

  if (mapRef != null && !map) {
    setMapRef(mapRef.current);
  }

  const source =
    areaLevel === "country"
      ? "world-countries"
      : areaLevel === "county"
        ? "eng-districts"
        : areaLevel === "city"
          ? "eng-cities"
          : "eng-boroughs";

  const handleClick = async (event: MapLayerMouseEvent) => {
    const { features } = event;
    const selectedFeature = features?.[0];
    setUserMarker(null);

    if (Object.hasOwn(selectedFeature?.properties ?? {}, "n")) {
      const id = selectedFeature.properties.id;
      const hasSearchMarker = markers.some((marker) => marker?.isSearch);
      setSelectedArea({
        name: selectedFeature.properties.n,
        id: id,
        type: areaLevel,
        removeAll: hasSearchMarker,
      });
      if (firstArea) setShowSecondGeocoder(true);
      const { lng, lat } = event.lngLat;
      const res = await axios.get(
        `${
          import.meta.env.VITE_API_URL
        }/stats/count/${id}?layerType=${areaLevel}&lat=${lat}&lng=${lng}&start=${
          lastReported[selectedTime].start
        }&end=${lastReported[selectedTime].end}`,
      );
      const { yearCounts, lastReportedMonthCount } = await res.data;
      const marker: MarkerType = {
        lng,
        lat,
        counts: {
          yearCounts,
          lastReportedMonthCount,
        },
        showPopup: true,
        isFirst: !firstArea || firstArea?.isSearch,
        location: selectedFeature.properties.n,
      };

      addMarker(marker);

      let zoom = getZoom("country", false) + 5;
      if (areaLevel !== "country") {
        if (areaLevel === "county") {
          zoom = getZoom("district", false) + 1;
        } else if (areaLevel === "city") {
          zoom = getZoom("city", false) + 1;
        } else {
          zoom = getZoom("borough", false) + 1;
        }
      }
      if (firstArea?.name === selectedFeature.properties.n) {
        zoom--;
      } else if (secondArea?.name === selectedFeature.properties.n) {
        zoom -= 0.5;
      }
      const newViewState = {
        ...viewState,
        zoom,
      };

      if (!firstArea)
        map?.flyTo({
          center: [lng, lat],
          duration: 2000,
          zoom,
        });

      if (!firstArea || hasSearchMarker)
        setViewState({
          ...newViewState,
          latitude: lat,
          longitude: lng,
        });
      else setViewState(newViewState);

      if (markers.length > 1) {
        const [prevMarker] = markers;
        setMarkers([prevMarker, marker]);
      } else {
        setMarkers([...markers, marker]);
      }

      const target = {
        source,
        id,
      };

      if (!firstArea && !secondArea)
        mapRef.current?.setFeatureState(target, {
          "selected-1": true,
        });
      else if (secondArea) {
        if (firstArea?.id === id) {
          mapRef.current?.setFeatureState(target, {
            "selected-1": false,
          });
          mapRef.current?.setFeatureState(
            {
              source,
              id: secondArea.id,
            },
            {
              "selected-2": false,
            },
          );
          return;
        }
        mapRef.current?.setFeatureState(target, {
          "selected-2": secondArea.id !== id,
        });
        mapRef.current?.setFeatureState(
          {
            source,
            id: secondArea.id,
          },
          {
            "selected-2": false,
          },
        );
      } else if (firstArea) {
        if (firstArea.id === id)
          mapRef.current?.setFeatureState(target, {
            "selected-1": false,
          });
        else
          mapRef.current?.setFeatureState(target, {
            "selected-2": true,
          });
      } else
        mapRef.current?.setFeatureState(target, {
          "selected-2": true,
        });
    }
  };

  const handleMouseMove = (e: MapLayerMouseEvent) => {
    if (e.features && e.features.length > 0) {
      if (hoveredStateIdRef.current) {
        mapRef.current?.setFeatureState(
          {
            source,
            id: hoveredStateIdRef.current,
          },
          {
            hover: false,
          },
        );
      }
      const newHoveredStateId = e.features[0].id as number;
      hoveredStateIdRef.current = newHoveredStateId;
      mapRef.current?.setFeatureState(
        {
          source,
          id: newHoveredStateId,
        },
        {
          hover: true,
        },
      );
    }
  };

  const handleViewStateChange = useCallback(
    ({ viewState, ...rest }: ViewStateChangeEvent) => {
      setViewState(viewState);
    },
    [setViewState],
  );

  useEffect(() => {
    const isSearch = markers.filter((el) => el?.isSearch).length > 0;
    if (areaLevel !== previousLevel && !isSearch) {
      setPreviousLevel(areaLevel);
      setMarkers([]);
    }
  }, [areaLevel, setMarkers]);

  return (
    <div className="left-0 top-0 h-screen w-screen">
      <ReactMapGL
        {...viewState}
        ref={mapRef}
        style={{
          position: "absolute",
          inset: 0,
          height: "100vh",
          width: "100vw",
        }}
        mapboxAccessToken={import.meta.env.VITE_MAPBOX_API_KEY}
        mapStyle={import.meta.env.VITE_MAPBOX_STYLE}
        onMove={handleViewStateChange}
        onMouseMove={handleMouseMove}
        onClick={handleClick}
        interactiveLayerIds={["world-countries", "eng-districts", "eng-cities", "london-boroughs"]}
      >
        <MapContent />
      </ReactMapGL>
    </div>
  );
};

export default Map;
